import React, { useEffect, useRef, useState } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import googleMapsLoader from '../utils/googleMapsLoader';

const TransportationRouteMap = ({ locations, className = '' }) => {
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const isInitializedRef = useRef(false);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Only initialize map when we have locations and haven't already initialized
    if (locations && locations.length > 0 && !isInitializedRef.current) {
      // Use setTimeout to ensure DOM is fully rendered and ref is attached
      const timer = setTimeout(() => {
        if (mapRef.current && !isInitializedRef.current) {
          initializeMap();
        } else if (!mapRef.current) {
          console.warn('Map ref not available after timeout');
          setError('Map container not available');
          setLoading(false);
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [locations]);

  useEffect(() => {
    if (map && locations && locations.length > 0) {
      renderRoute().catch(err => {
        console.error('Error rendering route:', err);
        setError('Failed to render route on map');
      });
    }
  }, [map, locations]);

  const initializeMap = async () => {
    try {
      // Check if map container exists
      if (!mapRef.current) {
        console.error('Map container element not found');
        setError('Map container not available');
        setLoading(false);
        return;
      }

      // Load Google Maps with directions library
      await googleMapsLoader.load(['directions']);
      
      if (!window.google || !window.google.maps) {
        throw new Error('Google Maps API failed to load');
      }

      // Double-check the ref is still available after async operations
      if (!mapRef.current) {
        console.error('Map container element lost after async operations');
        setError('Map container not available');
        setLoading(false);
        return;
      }

      // Helper function to check if coordinate is valid
      const isValidCoordinate = (coord) => {
        if (coord === null || coord === undefined || coord === '') {
          return false;
        }
        // Check for string "null" or "undefined"
        if (typeof coord === 'string' && (coord.toLowerCase() === 'null' || coord.toLowerCase() === 'undefined')) {
          return false;
        }
        const num = parseFloat(coord);
        return !isNaN(num) && num !== 0;
      };

      // Filter locations that have valid coordinates
      console.log('TransportationRouteMap - Received locations:', locations);
      console.log('TransportationRouteMap - Locations type:', typeof locations, 'Is array:', Array.isArray(locations));
      
      if (!locations || !Array.isArray(locations) || locations.length === 0) {
        console.warn('TransportationRouteMap - No locations provided or empty array');
        setError('No locations available');
        setLoading(false);
        return;
      }

      // Log each location's coordinates
      locations.forEach((loc, idx) => {
        console.log(`Location ${idx}:`, {
          location_type: loc.location_type,
          address: loc.address,
          latitude: loc.latitude,
          latitude_type: typeof loc.latitude,
          longitude: loc.longitude,
          longitude_type: typeof loc.longitude,
          hasValidCoords: isValidCoordinate(loc.latitude) && isValidCoordinate(loc.longitude)
        });
      });

      const validLocations = locations.filter(loc => {
        const hasValidCoords = isValidCoordinate(loc.latitude) && isValidCoordinate(loc.longitude);
        return hasValidCoords;
      });

      console.log('TransportationRouteMap - Valid locations with coordinates:', validLocations.length, 'out of', locations.length);

      // Initialize map - use first valid location if available, otherwise use a default center
      // We'll handle geocoding in renderRoute if coordinates are missing
      let mapCenter;
      let mapZoom = 10;
      
      if (validLocations.length > 0) {
        mapCenter = new window.google.maps.LatLng(
          parseFloat(validLocations[0].latitude),
          parseFloat(validLocations[0].longitude)
        );
        mapZoom = validLocations.length > 1 ? 10 : 12;
      } else {
        // Default center (can be adjusted) - we'll fit bounds after geocoding
        mapCenter = new window.google.maps.LatLng(34.0522, -118.2437); // Los Angeles default
        mapZoom = 10;
        console.log('TransportationRouteMap - No valid coordinates yet, using default center. Will geocode and update.');
      }

      // Initialize map
      const mapOptions = {
        zoom: mapZoom,
        center: mapCenter,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      };

      // Don't re-initialize if already done
      if (isInitializedRef.current && mapInstanceRef.current) {
        console.log('Map already initialized, skipping');
        setLoading(false);
        return;
      }

      const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
      
      // Initialize directions service and renderer
      const directionsSvc = new window.google.maps.DirectionsService();
      const directionsRndr = new window.google.maps.DirectionsRenderer({
        map: newMap,
        suppressMarkers: true, // We'll add custom markers
        polylineOptions: {
          strokeColor: '#3b82f6',
          strokeWeight: 5,
          strokeOpacity: 0.8
        }
      });

      mapInstanceRef.current = newMap;
      directionsRendererRef.current = directionsRndr;
      isInitializedRef.current = true;
      
      setMap(newMap);
      setDirectionsService(directionsSvc);
      setDirectionsRenderer(directionsRndr);
      setLoading(false);
    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to load map');
      setLoading(false);
    }
  };

  const buildCompleteAddress = (location) => {
    if (!location) return '';
    
    // Check if address already contains a zip code pattern
    if (location.address && /\b\d{5}\b/.test(location.address)) {
      return location.address;
    }
    
    // Build address from components
    const parts = [];
    if (location.address) parts.push(location.address);
    if (location.city) parts.push(location.city);
    if (location.state) parts.push(location.state);
    if (location.zip_code) parts.push(location.zip_code);
    return parts.join(', ');
  };

  const geocodeLocation = async (location) => {
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
      return null;
    }

    const address = buildCompleteAddress(location);
    if (!address) return null;

    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({
            lat: location.lat(),
            lng: location.lng()
          });
        } else {
          console.warn(`Failed to geocode address: ${address}`, status);
          resolve(null);
        }
      });
    });
  };

  const renderRoute = async () => {
    if (!map || !locations) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];
    setMarkers([]);

    // Clear existing directions
    if (directionsRendererRef.current) {
      try {
        directionsRendererRef.current.setDirections({ routes: [] });
      } catch (e) {
        console.warn('Error clearing directions:', e);
      }
    }

    // Helper function to check if coordinate is valid
    const isValidCoordinate = (coord) => {
      if (coord === null || coord === undefined || coord === '') {
        return false;
      }
      const num = parseFloat(coord);
      return !isNaN(num) && num !== 0;
    };

    // Process locations: filter valid ones and geocode missing coordinates
    const processedLocations = [];
    for (const loc of locations) {
      if (isValidCoordinate(loc.latitude) && isValidCoordinate(loc.longitude)) {
        // Already has valid coordinates
        processedLocations.push(loc);
      } else if (loc.address) {
        // Try to geocode if we have an address
        console.log('Geocoding location without coordinates:', buildCompleteAddress(loc));
        const coords = await geocodeLocation(loc);
        if (coords) {
          processedLocations.push({
            ...loc,
            latitude: coords.lat,
            longitude: coords.lng
          });
        } else {
          console.warn('Failed to geocode location:', buildCompleteAddress(loc));
        }
      }
    }

    // Sort by leg_sequence
    const validLocations = processedLocations.sort((a, b) => (a.leg_sequence || 0) - (b.leg_sequence || 0));

    if (validLocations.length === 0) {
      console.warn('No valid locations after processing');
      setError('Unable to determine location coordinates');
      return;
    }

    if (validLocations.length < 2 || !directionsService || !directionsRendererRef.current) {
      // If only one location, or directions not available, just show markers
      showMarkersOnly(validLocations);
      return;
    }

    // Build waypoints for multi-leg trips
    const waypoints = [];
    for (let i = 1; i < validLocations.length - 1; i++) {
      waypoints.push({
        location: new window.google.maps.LatLng(
          parseFloat(validLocations[i].latitude),
          parseFloat(validLocations[i].longitude)
        ),
        stopover: true
      });
    }

    // Calculate route
    const origin = new window.google.maps.LatLng(
      parseFloat(validLocations[0].latitude),
      parseFloat(validLocations[0].longitude)
    );
    
    const destination = new window.google.maps.LatLng(
      parseFloat(validLocations[validLocations.length - 1].latitude),
      parseFloat(validLocations[validLocations.length - 1].longitude)
    );

    const request = {
      origin: origin,
      destination: destination,
      waypoints: waypoints.length > 0 ? waypoints : undefined,
      optimizeWaypoints: false,
      travelMode: window.google.maps.TravelMode.DRIVING
    };

    directionsService.route(request, (result, status) => {
      if (status === window.google.maps.DirectionsStatus.OK) {
        // Check if renderer is still available (component might have unmounted)
        if (!directionsRendererRef.current || !mapRef.current) {
          console.warn('Map or directions renderer no longer available');
          return;
        }

        try {
          // Clear any existing directions first
          directionsRendererRef.current.setDirections({ routes: [] });
          
          // Set new directions
          directionsRendererRef.current.setDirections(result);
        } catch (e) {
          console.error('Error setting directions:', e);
          // Fallback to markers only
          showMarkersOnly(validLocations);
          return;
        }
        
        // Add custom markers for all locations
        const newMarkers = [];
        validLocations.forEach((location, index) => {
          const position = new window.google.maps.LatLng(
            parseFloat(location.latitude),
            parseFloat(location.longitude)
          );

          const marker = new window.google.maps.Marker({
            position: position,
            map: map,
            title: buildLocationLabel(location),
            icon: getMarkerIcon(location.location_type),
            label: {
              text: (index + 1).toString(),
              color: 'white',
              fontWeight: 'bold',
              fontSize: '12px'
            }
          });

          // Add info window
          const infoWindow = new window.google.maps.InfoWindow({
            content: buildInfoWindowContent(location)
          });

          marker.addListener('click', () => {
            infoWindow.open(map, marker);
          });

          newMarkers.push(marker);
        });

        // Fit bounds to show entire route
        const bounds = new window.google.maps.LatLngBounds();
        validLocations.forEach(location => {
          bounds.extend(new window.google.maps.LatLng(
            parseFloat(location.latitude),
            parseFloat(location.longitude)
          ));
        });
        map.fitBounds(bounds);

        markersRef.current = newMarkers;
        setMarkers(newMarkers);
      } else {
        console.error('Directions request failed:', status);
        // Fallback: just show markers without route
        showMarkersOnly(validLocations);
      }
    });
  };

  const showMarkersOnly = (validLocations) => {
    if (!map || !validLocations || validLocations.length === 0) return;

    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];

    const bounds = new window.google.maps.LatLngBounds();
    const newMarkers = [];

    validLocations.forEach((location, index) => {
      const position = new window.google.maps.LatLng(
        parseFloat(location.latitude),
        parseFloat(location.longitude)
      );

      bounds.extend(position);

      const marker = new window.google.maps.Marker({
        position: position,
        map: map,
        title: buildLocationLabel(location),
        icon: getMarkerIcon(location.location_type),
        label: {
          text: (index + 1).toString(),
          color: 'white',
          fontWeight: 'bold',
          fontSize: '12px'
        }
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: buildInfoWindowContent(location)
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });

      newMarkers.push(marker);
    });

    map.fitBounds(bounds);
    markersRef.current = newMarkers;
    setMarkers(newMarkers);
  };

  const getMarkerIcon = (locationType) => {
    const colors = {
      pickup: '#10b981', // green
      dropoff: '#3b82f6', // blue
      second_dropoff: '#8b5cf6' // purple
    };

    const color = colors[locationType] || '#6b7280'; // gray default

    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 3
    };
  };

  const buildLocationLabel = (location) => {
    const type = location.location_type === 'pickup' ? 'Pickup' :
                 location.location_type === 'dropoff' ? 'Dropoff' :
                 'Stop';
    return `${type}: ${location.address}`;
  };

  const buildInfoWindowContent = (location) => {
    const type = location.location_type === 'pickup' ? 'Pickup Location' :
                 location.location_type === 'dropoff' ? 'Dropoff Location' :
                 'Stop Location';
    
    const address = [
      location.address,
      location.city,
      location.state,
      location.zip_code
    ].filter(Boolean).join(', ');

    return `
      <div style="padding: 8px; min-width: 200px;">
        <div style="font-weight: bold; margin-bottom: 4px; color: ${
          location.location_type === 'pickup' ? '#10b981' :
          location.location_type === 'dropoff' ? '#3b82f6' : '#8b5cf6'
        };">
          ${type}
        </div>
        <div style="color: #374151; margin-bottom: 4px;">${address}</div>
        ${location.special_instructions ? 
          `<div style="color: #6b7280; font-size: 12px; margin-top: 8px; font-style: italic;">${location.special_instructions}</div>` 
          : ''}
      </div>
    `;
  };

  // Cleanup function
  const cleanup = () => {
    // Clear markers
    markersRef.current.forEach(marker => {
      if (marker) {
        marker.setMap(null);
      }
    });
    markersRef.current = [];

    // Clear directions renderer
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }

    // Reset initialization flag (but don't destroy map instance)
    // The map will be reused if component re-renders
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      isInitializedRef.current = false;
      mapInstanceRef.current = null;
    };
  }, []);

  if (!locations || locations.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 bg-gray-100 rounded-lg ${className}`}>
        <div className="text-gray-600">No locations available</div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Map container - Google Maps will manage its internal DOM */}
      <div className="relative w-full" style={{ minHeight: '400px' }}>
        <div 
          ref={mapRef} 
          className="w-full h-96 rounded-lg border border-gray-200 shadow-sm"
          style={{ minHeight: '400px' }}
        />
        {/* Loading and error states as overlays - positioned outside map container */}
        {loading && !isInitializedRef.current && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-lg z-10 pointer-events-none">
            <div className="text-gray-600">Loading map...</div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded-lg z-10 pointer-events-none">
            <div className="text-red-600 text-center px-4">{error}</div>
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap gap-4 items-center text-sm">
        <div className="flex items-center">
          <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
          <span className="text-gray-700">Pickup</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
          <span className="text-gray-700">Dropoff</span>
        </div>
        {locations.some(loc => loc.location_type === 'second_dropoff') && (
          <div className="flex items-center">
            <div className="w-4 h-4 rounded-full bg-purple-500 mr-2"></div>
            <span className="text-gray-700">Stop</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransportationRouteMap;

